import type { Chiffre } from './chiffrement.js';

export interface GardeStockage {
  readonly generation: number;
  readonly proprietaire: string | null;
  readonly autorisation: string | null;
}
export const GARDE_INITIALE: GardeStockage = {
  generation: 0,
  proprietaire: null,
  autorisation: null,
};
export function memeGarde(a: GardeStockage, b: GardeStockage): boolean {
  return (
    a.generation === b.generation &&
    a.proprietaire === b.proprietaire &&
    a.autorisation === b.autorisation
  );
}

/** Où l'appareil garde ses instantanés et leurs clés (M11). */
export interface MagasinHorsLigne {
  controle(): Promise<GardeStockage>;
  /** CAS après validation serveur : changement de propriétaire/droits = nouvelle génération + purge. */
  autoriser(
    attendue: number,
    proprietaire: string,
    autorisation: string,
    forcer: boolean,
  ): Promise<GardeStockage | null>;
  invalider(): Promise<void>;
  /** Comparaison + clé + chiffré dans UNE transaction commune aux onglets. */
  publier(garde: GardeStockage, cle: CryptoKey, chiffre: Chiffre): Promise<boolean>;
  surveiller(rappel: () => void): () => void;
  lireCle(id: string): Promise<CryptoKey | null>;
  ecrireCle(id: string, cle: CryptoKey): Promise<void>;
  lireInstantane(id: string): Promise<Chiffre | null>;
  ecrireInstantane(id: string, chiffre: Chiffre): Promise<void>;
  /** Clé D'ABORD (effacement cryptographique), puis l'instantané. */
  supprimer(id: string): Promise<void>;
  identifiants(): Promise<string[]>;
}

/** Magasin en mémoire : tests, et repli d'un navigateur sans IndexedDB. */
export function magasinMemoire(): MagasinHorsLigne {
  const cles = new Map<string, CryptoKey>();
  const instantanes = new Map<string, Chiffre>();
  let garde = GARDE_INITIALE;
  const abonnes = new Set<() => void>();
  function effacer() {
    cles.clear();
    instantanes.clear();
  }
  function annoncer() {
    for (const rappel of abonnes) rappel();
  }
  return {
    controle: async () => garde,
    autoriser: async (attendue, proprietaire, autorisation, forcer) => {
      if (garde.generation !== attendue) return null;
      if (forcer || garde.proprietaire !== proprietaire || garde.autorisation !== autorisation) {
        effacer();
        garde = { generation: garde.generation + 1, proprietaire, autorisation };
        annoncer();
      }
      return garde;
    },
    invalider: async () => {
      effacer();
      garde = { ...GARDE_INITIALE, generation: garde.generation + 1 };
      annoncer();
    },
    publier: async (attendue, cle, chiffre) => {
      if (!attendue.proprietaire || !memeGarde(garde, attendue)) return false;
      effacer();
      cles.set(attendue.proprietaire, cle);
      instantanes.set(attendue.proprietaire, chiffre);
      return true;
    },
    surveiller: (rappel) => {
      abonnes.add(rappel);
      return () => {
        abonnes.delete(rappel);
      };
    },
    lireCle: async (id) => cles.get(id) ?? null,
    ecrireCle: async (id, cle) => {
      cles.set(id, cle);
    },
    lireInstantane: async (id) => instantanes.get(id) ?? null,
    ecrireInstantane: async (id, chiffre) => {
      instantanes.set(id, chiffre);
    },
    supprimer: async (id) => {
      if (garde.proprietaire && garde.proprietaire !== id) return;
      effacer();
      garde = { ...GARDE_INITIALE, generation: garde.generation + 1 };
      annoncer();
    },
    identifiants: async () => [...new Set([...cles.keys(), ...instantanes.keys()])].sort(),
  };
}
